import { jsx as _jsx } from "react/jsx-runtime";
/**
 * SowDataTableNode - a read-only Tiptap atom block that shows a resolved SOW data
 * table (a dynamicBlock) INSIDE the prose editor, so the user sees the data tables
 * while editing the narrative. It is non-editable and is NEVER serialized into
 * sowContent (the shared `tiptapToMarkdown` drops `sowDataTable`, and the server
 * recomputes sowContent from tiptapContent on save - so it can't leak into prose).
 *
 * The table itself is rendered by the HOST (apps/web, via DocContractView) through
 * a render callback supplied on React context, because the renderer lives outside
 * this package. The node carries only the `dataKey`.
 */
import * as React from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
const SowDataTableRendererContext = React.createContext(null);
export function SowDataTableRendererProvider({ renderer, children, }) {
    return (_jsx(SowDataTableRendererContext.Provider, { value: renderer, children: children }));
}
function SowDataTableNodeView({ node }) {
    const dataKey = String(node.attrs.dataKey ?? '');
    const includeHeading = !!node.attrs.includeHeading;
    const badge = String(node.attrs.badge || 'Generated table - read-only');
    const renderer = React.useContext(SowDataTableRendererContext);
    const content = renderer ? renderer({ dataKey, includeHeading, badge }) : null;
    // No "read-only" badge: data tables are now cell-editable, and the editable hint/Updated badge
    // live in the table's own header line (DocContractView EditableDataBlock).
    return (_jsx(NodeViewWrapper, { as: "div", className: "sow-data-table-nodeview", contentEditable: false, "data-sow-data-table": dataKey, children: content }));
}
export const SowDataTableNode = Node.create({
    name: 'sowDataTable',
    group: 'block',
    inline: false,
    atom: true,
    selectable: false,
    draggable: false,
    addAttributes() {
        return {
            // The persisted identity. The table data is rendered live from the
            // render-context (no per-deal rows baked into the editor JSON).
            dataKey: { default: '' },
            includeHeading: { default: false },
            badge: { default: 'Generated table - read-only' },
        };
    },
    parseHTML() {
        return [{ tag: 'div[data-sow-data-table]' }];
    },
    renderHTML({ HTMLAttributes }) {
        // Clipboard/export HTML only; never used for sowContent (markdown).
        return ['div', mergeAttributes(HTMLAttributes, { 'data-sow-data-table': '' })];
    },
    // Deliberately NO renderText() - getText() yields nothing for this node, keeping
    // dirty/plain-text comparisons prose-only.
    addNodeView() {
        return ReactNodeViewRenderer(SowDataTableNodeView);
    },
});
//# sourceMappingURL=sowDataTableNode.js.map